import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CarouselProvider,
  Slider,
  ButtonBack,
  ButtonNext,
} from 'pure-react-carousel';
import { SLIDE_HEIGHT, SLIDE_WIDTH } from '../../shared';
import IconLeft from '../IconLeft';
import IconRight from '../IconRight';
import s from './CarouselVisualNav.scss';
import RenderSlideVisualNav, {
  RenderFirstCard,
} from '../RenderSlideVisualNav/RenderSlideVisualNav';
import ImageWatcher from '../ImageWatcher';
import { CarouselCards, FirstCard, VideoPlayingState } from '../../Carousel';
// Max limit visible slides in Carousel by device type.
const MAX_VISIBLE_SLIDES_DESKTOP = 6;
const MAX_VISIBLE_SLIDES_MOBILE = 2.5;

const ColumnCardSize = {
  single: 1,
  double: 2,
  triple: 3,
};

export type CarouselVisualNavProps = {
  /** An array of carousel card objects. This value is currently used for Category and visualNav Carousel. */
  cards?: CarouselCards[];
  /** Moves the nth slide into the leftmost position of the carousel */
  currentSlide?: number;
  /** Switch the applied css rules and change the component rendering based on the device. */
  deviceType: 'mobile' | 'desktop';
  firstCard?: FirstCard;
  handlePlayPauseClick?: (filterValue: string) => void;
  slideWidth: number;
  useVideoPlayer?: boolean;
  videoPlayingState?: VideoPlayingState[];
  mainImages?: never;
};

export type CarouselVisualNavPropsInjectedByCarousel = {
  onClick: (i: string, ev: React.MouseEvent<HTMLElement>) => void;
  /**  a unique id for the carousel. This number is used to help create unique css id's and class
  names so that you can have more than one carousel on the page and style them differently,
  or reference a dom element via their unique id attribute name via javascript.
  This number is generated automatically in Carousel and passed in. */
  uid: number;
};

export function getTotalSlides(hasFirstCard: boolean, cards: CarouselCards[]) {
  const totalCards = cards.reduce((total, currentCard) => {
    const columnSize =
      currentCard.type && ColumnCardSize[currentCard.type]
        ? ColumnCardSize[currentCard.type]
        : ColumnCardSize.single;
    return columnSize + total;
  }, 0);
  return hasFirstCard ? totalCards + 1 : totalCards;
}

export function shouldDisplayFirstCard(firstCard: FirstCard) {
  return !!Object.keys(firstCard).length && !!firstCard.image;
}

export function computeMaxSlides(
  isDesktopDevice: boolean,
  containerWidth: number,
  slideWidth: number,
  totalSlides: number
) {
  if (!isDesktopDevice) {
    return Math.min(totalSlides, MAX_VISIBLE_SLIDES_MOBILE);
  }

  return Math.min(
    totalSlides,
    MAX_VISIBLE_SLIDES_DESKTOP,
    Math.floor(containerWidth / slideWidth)
  );
}

const CarouselVisualNav = ({
  cards = [],
  currentSlide = 0,
  deviceType,
  firstCard = {},
  handlePlayPauseClick,
  onClick,
  slideWidth,
  uid,
  useVideoPlayer = false,
  videoPlayingState,
}: CarouselVisualNavProps & CarouselVisualNavPropsInjectedByCarousel) => {
  const sliderParentRef = useRef<HTMLDivElement>(null);
  const [maxVisibleSlidesForDeviceType, setMaxVisibleSlidesForDeviceType] =
    useState(1);

  const hasFirstCard = shouldDisplayFirstCard(firstCard);
  const images = cards.map((card) => card.imageUrl);

  const isDesktopDevice = deviceType === 'desktop';

  const totalSlides = getTotalSlides(hasFirstCard, cards);
  const isLimitSlidesExceeded = totalSlides > maxVisibleSlidesForDeviceType;

  const sliderClassName = isDesktopDevice
    ? s.sliderTrayDesktop
    : s.sliderTrayMobile;

  useEffect(() => {
    const handleResize: ResizeObserverCallback = (entries) => {
      for (const entry of entries) {
        const max = computeMaxSlides(
          isDesktopDevice,
          entry.contentRect.width,
          slideWidth,
          totalSlides
        );
        setMaxVisibleSlidesForDeviceType(max);
      }
    };
    const observer = new ResizeObserver(handleResize);

    if (sliderParentRef.current) {
      observer.observe(sliderParentRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isDesktopDevice, slideWidth, totalSlides]);

  const wrapperStyles = useMemo<React.CSSProperties>(
    () => ({ maxWidth: `${maxVisibleSlidesForDeviceType * slideWidth}px` }),
    [maxVisibleSlidesForDeviceType, slideWidth]
  );

  return (
    <div data-testid="sliderParent" ref={sliderParentRef}>
      <div className={s.centeredContainerWrapper} style={wrapperStyles}>
        <CarouselProvider
          className="carousel--centered"
          currentSlide={currentSlide}
          data-testid="CarouselVisualNav"
          data-uid={uid}
          dragEnabled={false}
          lockOnWindowScroll
          naturalSlideHeight={SLIDE_HEIGHT}
          naturalSlideWidth={SLIDE_WIDTH}
          totalSlides={totalSlides}
          visibleSlides={maxVisibleSlidesForDeviceType}
        >
          <ImageWatcher
            alwaysHonorCurrentSlide
            filterID={cards?.[0]?.filterId}
            images={images}
            isVisualNav
          />
          <div className={s.sliderButtonContainer}>
            <Slider classNameTray={sliderClassName} trayTag="div">
              {hasFirstCard && (
                <RenderFirstCard
                  size={slideWidth}
                  deviceType={deviceType}
                  cardData={firstCard}
                />
              )}
              {cards.map((card, i) => (
                <RenderSlideVisualNav
                  categoryCardId={card.id}
                  deviceType={deviceType}
                  filterId={card.filterId}
                  filterValue={card.filterValue}
                  handlePlayPauseClick={handlePlayPauseClick}
                  i={i}
                  imageDescription={card.imageDescription}
                  imageTitle={card.imageTitle}
                  isDisabled={card.isDisabled}
                  isLazyLoaded={i > maxVisibleSlidesForDeviceType - 1}
                  isSelectedFacet={card.isSelectedFacet}
                  key={`${card.filterId}_${card.filterValue}_${card.id}`}
                  mainImage={card.imagePath}
                  mainVideo={card.videoPath}
                  // NOTE: onClick here is passed a string value for the card.filterValue
                  // in all other Carousel sub components, onClick is passed the current
                  // slide number.
                  onClick={onClick}
                  size={slideWidth}
                  uid={uid}
                  useVideoPlayer={useVideoPlayer}
                  videoState={
                    videoPlayingState?.find(
                      (items) => items.id === card.filterValue
                    )?.videoState
                      ? /* istanbul ignore next - too complicated to test. Code Smell!! */
                        videoPlayingState.find(
                          /* istanbul ignore next - too complicated to test. Code Smell!! */
                          (items) => items.id === card.filterValue
                        )?.videoState
                      : 'stop'
                  }
                />
              ))}
            </Slider>
            {deviceType === 'desktop' && isLimitSlidesExceeded && (
              <>
                <ButtonBack className={s.back}>
                  <IconLeft />
                </ButtonBack>
                <ButtonNext className={s.next}>
                  <IconRight />
                </ButtonNext>
              </>
            )}
          </div>
        </CarouselProvider>
      </div>
    </div>
  );
};

export default CarouselVisualNav;
